import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { usePostHog } from 'src/lib/posthog';
import { toast } from 'src/components/ui/toast';
import {
  isPendingKey,
  isShownTab,
  tabKey,
  type CanvasTab,
  type CanvasDocumentHandle,
  type MountedPanel,
  type PendingMode,
  type ShownTab,
  TurnLock,
  type ViewState
} from '../components/editorPanelTypes';
import {
  canvasTabsReducer,
  emptyCanvasTabs,
  findPending,
  nextPendingKey,
  rememberedArrangement,
  shownPanelKey,
  type CanvasTabsState,
  type TabArrangement
} from './canvasTabsReducer';
import { loadRememberedArrangement, saveRememberedArrangement } from './canvasTabsStorage';
import { getCanvases } from '../api';
import {
  CANVAS_CREATE_TOOLS,
  CANVAS_EDIT_TOOLS,
  CANVAS_TOOL_NAMES,
  editorCanvasQueryKey,
  editorCanvasesQueryKey,
  editorSessionCanvasVersionsQueryKey
} from '../constants';
import type { CanvasSummary, CreatedCanvas, Message, ParentPart, ToolCallPart } from '../types';
import { CANVAS_PARAM, CANVAS_VERSION_PARAM } from '../urls';

interface UseCanvasEditorOptions {
  isEditorEnabled: boolean;
  /** Gates the tab bar's data behind the `canvas-tabs` feature flag: the chat's canvas list and its remembered tabs. */
  canvasTabsEnabled: boolean;
  sessionId: string | undefined;
  isReadOnly?: boolean;
  /** True during a Turn, when no Pending Canvas may be opened. */
  isResponding: boolean;
  onCanvasCreated?: () => void;
  /** The assistant message being streamed, whose running tool calls reveal a canvas edit. */
  streamingMessage: Message | null;
  isStreamRunning: boolean;
  /**
   * Called when saving a Pending Canvas created a new session; `search` selects the new canvas,
   * titled `documentTitle`.
   */
  onSessionCreated?: (sessionId: string, search: string, documentTitle: string) => void;
  /** Called with the title of a Pending Canvas saved into the chat's existing session. */
  onPendingSavedInSession?: (documentTitle: string) => void;
}

interface UseCanvasEditorReturn {
  /** The saved canvas the panel shows, as in the URL; null while it shows the Pending Canvas or is closed. */
  activeCanvasId: string | null;
  isPanelOpen: boolean;
  /** False while there is nothing the panel could show and no Pending Canvas may be opened. */
  canOpenPanel: boolean;
  /** The chat's Closed Documents: its Canvases whose tabs are closed, in creation order. */
  closedDocuments: readonly CanvasTab[];
  /** The open tabs, in bar order. */
  tabs: readonly CanvasTab[];
  /** The tab the panel shows, or null while it is closed. */
  shownTab: ShownTab | null;
  /** The editors mounted so far: each tab's is mounted the first time it is shown, then kept. */
  panels: readonly MountedPanel[];
  /** The key of the shown tab's panel, or null while none is shown. */
  shownPanelKey: string | null;
  registerDocument: (panelKey: string, panel: CanvasDocumentHandle | null) => void;
  /** Pending read-only intent handed to the panels as a prop. */
  pendingMode: PendingMode | null;
  /** What the shown panel is showing or fetching, or null. Drives the card toggles and spinner. */
  activeMode: ViewState | null;
  /** Show a canvas's tab on its live content, opening the panel if it is closed. */
  handleOpenEditor: (canvasId: string) => void;
  handleSelectTab: (tab: CanvasTab) => void;
  /** Open the canvas straight into read-only diff mode for a version pair. */
  handleShowChanges: (canvasId: string, from: number, to: number) => void;
  /** Open the canvas straight into a read-only view of one version. */
  handleViewVersion: (canvasId: string, versionId: number) => void;
  /** Leave whichever read-only mode the shown panel is in, from a chat card. */
  handleExitReadOnly: () => void;
  /** Shown panel callback: record what it is currently showing or fetching. */
  handleViewChange: (view: ViewState | null, panelKey: string | null) => void;
  /** Hide the panel; its editors and the Pending Canvas are kept. */
  handleCloseEditor: () => void;
  /** Show the last-shown tab, the newest Canvas, or a new Pending Canvas. */
  handleOpenPanel: () => void;
  /** Open and show a new blank Pending Canvas. */
  handleNewDocument: () => void;
  /** Close a tab; a Pending Canvas's is discarded, confirming first if its content was edited. */
  handleCloseTab: (tab: CanvasTab) => void;
  /** Put the open tabs in a new order, given as their keys. */
  handleReorderTabs: (keys: readonly string[]) => void;
  handlePendingTitleChange: (key: string, title: string) => void;
  /** Switch to the canvas a Pending Canvas, named by its key, became on its first save. */
  handlePendingCanvasSaved: (key: string, created: CreatedCanvas) => void;
  /** Keep this chat's documents when its first message creates its session. */
  adoptCreatedSession: (sessionId: string) => void;
  /** Return the current URL's search for a URL that carries it on, less any `?canvas=` naming a dead Pending Canvas. */
  getSearchToCarry: () => string;
  /** Abandon every document editor, as leaving the chat does; the caller has already confirmed. */
  abandonDocuments: () => void;
  /** Confirm with the user if any mounted editor has unsaved content edits. */
  confirmDiscardUnsavedChanges: () => boolean;
  /**
   * Save the shown editor if it has unsaved edits, or the shown Pending Canvas even without any.
   * Resolves with the saved Pending Canvas when there was one; the caller then
   * calls `handlePendingCanvasSaved`. Throws on failure.
   */
  saveEditorBeforeSend: () => Promise<SavedPendingCanvas | null>;
  /** Get canvas context to attach to a chat request, or null if no editor is active */
  getCanvasContext: () => { editor_canvas_id: string } | null;
  /** Called by the stream handler when a canvas tool call completes */
  handleCanvasToolComplete: (toolName: string, metadata: Record<string, any>) => void;
  /** Lock on the active canvas for the turn being streamed; `editing` lasts until the edit's new content has loaded. */
  turnLock: TurnLock;
}

const isToolCallPart = (part: ParentPart): part is ToolCallPart => part.type === 'tool_call';

// The backend upper-cases canvas ids, so the model may name one in any case.
const isSameCanvasId = (candidate: unknown, canvasId: string) =>
  typeof candidate === 'string' && candidate.toUpperCase() === canvasId.toUpperCase();

const isRunningEditOf = (canvasId: string) => (part: ParentPart) =>
  isToolCallPart(part) &&
  part.status === 'running' &&
  CANVAS_EDIT_TOOLS.has(part.name) &&
  isSameCanvasId(part.arguments?.canvas_id, canvasId);

const DISCARD_PENDING_CONFIRMATION = 'Discard this new document? Your changes to it will be lost.';
const DISCARD_PENDINGS_CONFIRMATION = 'Discard your new documents? Your changes to them will be lost.';

/** A Pending Canvas's first save: its key, and the canvas it became. */
export interface SavedPendingCanvas {
  readonly key: string;
  readonly created: CreatedCanvas;
}

const pendingTab = (key: string): ShownTab => ({ kind: 'pending', key });
const savedTab = (canvasId: string): ShownTab => ({ kind: 'saved', canvasId });
// Shared, so the documents derived from the list keep their identity while it loads.
const NO_CANVASES: CanvasSummary[] = [];

const hasCanvas = (canvases: readonly CanvasSummary[], canvasId: string) => canvases.some(({ id }) => id === canvasId);

/** Return the `?canvasVersion=` value as a version number, or null unless it is a positive integer. */
function parseVersionId(raw: string | null): number | null {
  const versionId = Number(raw);
  return raw !== null && Number.isInteger(versionId) && versionId >= 1 ? versionId : null;
}

/** Return a copy of `params` naming `tab`, and `versionId` if the tab is a saved canvas's. */
function withUrlTab(params: URLSearchParams, tab: ShownTab | null, versionId: number | null): URLSearchParams {
  const next = new URLSearchParams(params);
  if (tab === null) {
    next.delete(CANVAS_PARAM);
  } else {
    next.set(CANVAS_PARAM, tabKey(tab));
  }
  if (tab?.kind === 'saved' && versionId !== null) {
    next.set(CANVAS_VERSION_PARAM, String(versionId));
  } else {
    next.delete(CANVAS_VERSION_PARAM);
  }
  return next;
}

/** Return `search` less a `?canvas=` naming a Pending Canvas the chat no longer has. */
function withoutDeadPending(search: string, state: CanvasTabsState): string {
  const params = new URLSearchParams(search);
  const raw = params.get(CANVAS_PARAM);
  if (raw === null || !isPendingKey(raw) || findPending(state, raw)) return search;
  const rest = withUrlTab(params, null, null).toString();
  return rest ? `?${rest}` : '';
}

/**
 * Return the open tabs in bar order. A document the arrangement does not
 * place yet, as before it is synced, follows the placed ones.
 */
function openTabs(documents: readonly CanvasTab[], { order, closed }: TabArrangement): readonly CanvasTab[] {
  const position = new Map(order.map((key, index) => [key, index]));
  const positionOf = (tab: CanvasTab) => position.get(tabKey(tab)) ?? order.length;
  // A stable sort keeps the documents `order` does not place in creation order.
  return documents
    .filter((document) => !closed.includes(tabKey(document)))
    .toSorted((a, b) => positionOf(a) - positionOf(b));
}

/** Return the tab to show once `key`'s tab goes: the one after it, else the one before. */
function neighbourOf(tabs: readonly CanvasTab[], key: string): CanvasTab | undefined {
  const index = tabs.findIndex((tab) => tabKey(tab) === key);
  return index === -1 ? undefined : (tabs[index + 1] ?? tabs[index - 1]);
}

/** Return `preferredId` if that canvas is still listed, else the newest canvas's id, if any. */
const listedOrNewest = (canvases: readonly CanvasSummary[], preferredId: string | null): string | undefined =>
  preferredId !== null && hasCanvas(canvases, preferredId) ? preferredId : canvases.at(-1)?.id;

const canvasListQuery = (sessionId: string | undefined) =>
  queryOptions({ queryKey: editorCanvasesQueryKey(sessionId), queryFn: () => getCanvases(sessionId!) });

/**
 * Return the tab `?canvas=` names. A Pending Canvas named there that no longer
 * exists, as after a reload abandons it, names nothing.
 */
function parseShownTab(raw: string | null, state: CanvasTabsState): ShownTab | null {
  if (raw === null) return null;
  if (!isPendingKey(raw)) return savedTab(raw);
  return findPending(state, raw) ? pendingTab(raw) : null;
}

/** ChatLayout registers this so navbar chat-history / new-chat can prompt before unmount. */
let registeredConfirmDiscardUnsavedChanges: (() => boolean) | null = null;

export function confirmDiscardUnsavedChanges(): boolean {
  return registeredConfirmDiscardUnsavedChanges?.() ?? true;
}

export default function useCanvasEditor({
  isEditorEnabled,
  canvasTabsEnabled,
  sessionId,
  isReadOnly,
  isResponding,
  onCanvasCreated,
  streamingMessage,
  isStreamRunning,
  onSessionCreated,
  onPendingSavedInSession
}: UseCanvasEditorOptions): UseCanvasEditorReturn {
  const posthog = usePostHog();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabsState, dispatch] = useReducer(canvasTabsReducer, sessionId, (initialSessionId) =>
    emptyCanvasTabs(initialSessionId, 0, loadRememberedArrangement(initialSessionId))
  );
  const { pendings } = tabsState;

  const canvasParam = searchParams.get(CANVAS_PARAM);
  const urlTab = useMemo(() => parseShownTab(canvasParam, tabsState), [canvasParam, tabsState]);
  const activeCanvasId = urlTab?.kind === 'saved' ? urlTab.canvasId : null;
  const urlVersionId = parseVersionId(searchParams.get(CANVAS_VERSION_PARAM));
  // The pane only exists once the editor is enabled, however the URL got its canvas.
  const shownTab = isEditorEnabled ? urlTab : null;
  const isPanelOpen = shownTab !== null;

  const canListCanvases = isEditorEnabled && canvasTabsEnabled;
  const { data: canvases = NO_CANVASES, isSuccess: isCanvasListLoaded } = useQuery({
    ...canvasListQuery(sessionId),
    enabled: canListCanvases && !!sessionId
  });

  const documents = useMemo((): readonly CanvasTab[] => {
    const saved: CanvasTab[] = canvases.map(({ id, title }) => ({ kind: 'saved', canvasId: id, title }));
    // A canvas the assistant just created is shown before the refetched list includes it.
    if (activeCanvasId !== null && !hasCanvas(canvases, activeCanvasId)) {
      saved.push({ kind: 'saved', canvasId: activeCanvasId, title: undefined });
    }
    return [
      ...saved,
      ...pendings.map(({ key, pendingTitle }): CanvasTab => ({ kind: 'pending', key, title: pendingTitle }))
    ];
  }, [canvases, activeCanvasId, pendings]);
  const { arrangement } = tabsState;
  const tabs = useMemo(() => openTabs(documents, arrangement), [documents, arrangement]);
  const closedDocuments = useMemo(
    () => documents.filter((document) => arrangement.closed.includes(tabKey(document))),
    [documents, arrangement]
  );

  useLayoutEffect(() => {
    const listedIds = documents.flatMap((document) => (document.kind === 'saved' ? [document.canvasId] : []));
    dispatch({ type: 'syncCanvases', listedIds, isComplete: isCanvasListLoaded });
  }, [documents, isCanvasListLoaded]);

  const rememberedSessionId = tabsState.sessionId;
  useEffect(() => {
    if (canvasTabsEnabled && rememberedSessionId !== undefined)
      saveRememberedArrangement(rememberedSessionId, rememberedArrangement(arrangement));
  }, [canvasTabsEnabled, rememberedSessionId, arrangement]);

  const panels = useMemo(
    (): readonly MountedPanel[] => [...tabsState.canvasPanels, ...pendings],
    [tabsState.canvasPanels, pendings]
  );

  const currentPanelKey = shownTab === null ? null : shownPanelKey(tabsState, shownTab);

  // Viewers who cannot add documents never get a Pending Canvas; nor does a chat mid-Turn.
  const canOpenPanel = isPanelOpen || documents.length > 0 || (!isReadOnly && !isResponding);

  // The latest render's values, for the returned callbacks to read without changing identity.
  const latest = {
    tabsState,
    tabs,
    canvases,
    urlTab,
    activeCanvasId,
    urlVersionId,
    shownTab,
    currentPanelKey,
    sessionId,
    searchParams,
    isEditorEnabled,
    canListCanvases,
    isReadOnly,
    onCanvasCreated,
    onSessionCreated,
    onPendingSavedInSession,
    setSearchParams
  };
  const latestRef = useRef(latest);
  latestRef.current = latest;

  // Imperative handles of the mounted editors, by panel key.
  const documentHandlesRef = useRef(new Map<string, CanvasDocumentHandle>());
  const registerDocument = useCallback((panelKey: string, panel: CanvasDocumentHandle | null) => {
    if (panel) {
      documentHandlesRef.current.set(panelKey, panel);
    } else {
      documentHandlesRef.current.delete(panelKey);
    }
  }, []);
  const getPanel = useCallback(
    (panelKey: string | null) => (panelKey === null ? undefined : documentHandlesRef.current.get(panelKey)),
    []
  );
  const getShownPanel = useCallback(() => getPanel(latestRef.current.currentPanelKey), [getPanel]);
  const getCanvasPanel = useCallback(
    (canvasId: string) => getPanel(shownPanelKey(latestRef.current.tabsState, savedTab(canvasId))),
    [getPanel]
  );
  const isPanelDirty = useCallback((panelKey: string) => !!documentHandlesRef.current.get(panelKey)?.isDirty(), []);

  const [refetchingEditedCanvasIds, setRefetchingEditedCanvasIds] = useState<readonly string[]>([]);

  // Only one URL write per event: each is computed from the last render's params,
  // so a second would overwrite the first.
  const writeUrlTab = useCallback((tab: ShownTab | null, versionId: number | null = null) => {
    latestRef.current.setSearchParams((prev) => withUrlTab(prev, tab, versionId), { replace: true });
  }, []);

  // Suppresses auto-open after the user explicitly closes the editor
  const userClosedEditorRef = useRef(false);

  /** Return the version a canvas's mounted editor shows read-only, or null. */
  const getViewedVersionId = useCallback(
    (canvasId: string) => getCanvasPanel(canvasId)?.getViewedVersionId() ?? null,
    [getCanvasPanel]
  );

  /**
   * Show a saved canvas's tab; the Pending Canvas, if any, moves to the background.
   * Without a version, the URL carries the one its editor still shows, if any.
   */
  const showCanvas = useCallback(
    (canvasId: string, versionId: number | null = getViewedVersionId(canvasId)) => {
      userClosedEditorRef.current = false;
      writeUrlTab(savedTab(canvasId), versionId);
    },
    [getViewedVersionId, writeUrlTab]
  );

  // A read-only intent for the panel. A fresh object per request so the panel
  // re-consumes even the same version or pair after an exit.
  const [pendingMode, setPendingMode] = useState<PendingMode | null>(() =>
    activeCanvasId !== null && urlVersionId !== null
      ? { canvasId: activeCanvasId, kind: 'viewing-version', versionId: urlVersionId }
      : null
  );

  // What the shown panel is actually showing (or fetching) right now, reported
  // back by it. Distinct from pendingMode (the intent): this reflects in-panel
  // exits and the header popover too, so the card toggles stay truthful however
  // the mode was reached, and it never claims a version is up while it is still loading.
  const [activeMode, setActiveMode] = useState<ViewState | null>(null);

  const confirmDiscardUnsavedChanges = useCallback((): boolean => {
    if (![...documentHandlesRef.current.values()].some((panel) => panel.isDirty())) return true;
    return window.confirm('You have unsaved changes to your document. Are you sure you want to leave?');
  }, []);

  useEffect(() => {
    registeredConfirmDiscardUnsavedChanges = confirmDiscardUnsavedChanges;
    return () => {
      if (registeredConfirmDiscardUnsavedChanges === confirmDiscardUnsavedChanges) {
        registeredConfirmDiscardUnsavedChanges = null;
      }
    };
  }, [confirmDiscardUnsavedChanges]);

  const capture = useCallback(
    (event: string, properties: Readonly<Record<string, unknown>> = {}) =>
      posthog?.capture(event, { sessionId: latestRef.current.sessionId, ...properties }),
    [posthog]
  );
  const captureCanvasOpened = useCallback((canvasId: string) => capture('canvas_opened', { canvasId }), [capture]);

  // Only a return to a tab restores the version it was left on; any other open lands on live.
  const showCanvasLive = useCallback(
    (canvasId: string) => {
      setPendingMode(null);
      getCanvasPanel(canvasId)?.exitReadOnly();
      showCanvas(canvasId, null);
    },
    [getCanvasPanel, showCanvas]
  );

  const handleOpenEditor = useCallback(
    (canvasId: string) => {
      captureCanvasOpened(canvasId);
      showCanvasLive(canvasId);
    },
    [captureCanvasOpened, showCanvasLive]
  );

  const returnToCanvas = useCallback(
    (canvasId: string) => {
      captureCanvasOpened(canvasId);
      setPendingMode(null);
      showCanvas(canvasId);
    },
    [captureCanvasOpened, showCanvas]
  );

  const handleShowChanges = useCallback(
    (canvasId: string, from: number, to: number) => {
      captureCanvasOpened(canvasId);
      capture('canvas_show_changes', { canvasId, from, to, entryPoint: 'card_header' });
      showCanvas(canvasId, null);
      setPendingMode({ canvasId, kind: 'diffing', from, to });
    },
    [capture, captureCanvasOpened, showCanvas]
  );

  const handleViewVersion = useCallback(
    (canvasId: string, versionId: number) => {
      captureCanvasOpened(canvasId);
      capture('canvas_version_selected', { canvasId, versionId, source: 'card' });
      showCanvas(canvasId, versionId);
      setPendingMode({ canvasId, kind: 'viewing-version', versionId });
    },
    [capture, captureCanvasOpened, showCanvas]
  );

  const handleExitReadOnly = useCallback(() => {
    getShownPanel()?.exitReadOnly();
  }, [getShownPanel]);

  // The panel whose report came last, to tell a change of its view from a switch of tab.
  const reportedPanelKeyRef = useRef<string | null>(null);

  // The panel is the source of truth for what it shows; mirror its report here,
  // and in the URL, so a reload reopens the same view: only a shown version is
  // carried there, and live drops it so Back to latest does not reopen the old one.
  const handleViewChange = useCallback(
    (view: ViewState | null, panelKey: string | null) => {
      setActiveMode(view);
      const isSamePanel = panelKey !== null && panelKey === reportedPanelKeyRef.current;
      reportedPanelKeyRef.current = panelKey;
      const { activeCanvasId, urlVersionId } = latestRef.current;
      // Showing a tab put its view in the URL already, and a fetch has not changed the view yet.
      if (!isSamePanel || activeCanvasId === null || view?.kind === 'loading-version') return;
      const versionId = view?.kind === 'viewing-version' && view.canvasId === activeCanvasId ? view.versionId : null;
      if (versionId !== urlVersionId) writeUrlTab(savedTab(activeCanvasId), versionId);
    },
    [writeUrlTab]
  );

  const handleCloseEditor = useCallback(() => {
    if (!confirmDiscardUnsavedChanges()) return;
    for (const panel of documentHandlesRef.current.values()) {
      if (panel.isDirty()) panel.discard();
    }
    const { activeCanvasId, shownTab } = latestRef.current;
    capture('canvas_closed', { canvasId: activeCanvasId });
    userClosedEditorRef.current = true;
    dispatch({ type: 'closePanel', shown: shownTab });
    writeUrlTab(null);
  }, [capture, confirmDiscardUnsavedChanges, writeUrlTab]);

  const showTab = useCallback(
    (tab: ShownTab) => {
      switch (tab.kind) {
        case 'saved':
          returnToCanvas(tab.canvasId);
          break;
        case 'pending':
          userClosedEditorRef.current = false;
          writeUrlTab(tab);
          break;
      }
    },
    [returnToCanvas, writeUrlTab]
  );

  const handleNewDocument = useCallback(() => {
    capture('canvas_new_document_opened');
    userClosedEditorRef.current = false;
    // Read before the dispatch applies, so it names the Pending Canvas the dispatch opens.
    const key = nextPendingKey(latestRef.current.tabsState);
    dispatch({ type: 'openPending' });
    writeUrlTab(pendingTab(key));
  }, [capture, writeUrlTab]);

  const isOpenAwaitingListRef = useRef(false);
  const handleOpenPanel = useCallback(() => {
    if (isOpenAwaitingListRef.current) return;
    const { tabsState, sessionId, canListCanvases } = latestRef.current;
    const { lastShown } = tabsState;
    if (lastShown?.kind === 'pending' && findPending(tabsState, lastShown.key)) {
      showTab(lastShown);
      return;
    }
    const openListedOrNew = (canvases: readonly CanvasSummary[]) => {
      const { lastShown: preferred } = latestRef.current.tabsState;
      const canvasId = listedOrNewest(canvases, preferred?.kind === 'saved' ? preferred.canvasId : null);
      if (canvasId === undefined) {
        handleNewDocument();
      } else {
        returnToCanvas(canvasId);
      }
    };
    if (!canListCanvases || !sessionId) {
      openListedOrNew(latestRef.current.canvases);
      return;
    }
    // Choosing before the list loads would open a Pending Canvas in a chat that already has documents.
    isOpenAwaitingListRef.current = true;
    void queryClient
      .ensureQueryData(canvasListQuery(sessionId))
      .catch(() => latestRef.current.canvases)
      .then((canvases) => {
        if (latestRef.current.sessionId === sessionId) openListedOrNew(canvases);
      })
      .finally(() => {
        isOpenAwaitingListRef.current = false;
      });
  }, [queryClient, showTab, handleNewDocument, returnToCanvas]);

  const handleSelectTab = useCallback(
    (tab: CanvasTab) => {
      const { shownTab } = latestRef.current;
      if (!isShownTab(shownTab, tabKey(tab))) showTab(tab);
    },
    [showTab]
  );

  const handleCloseTab = useCallback(
    (tab: CanvasTab) => {
      const { tabs, shownTab } = latestRef.current;
      if (tabs.length <= 1) return;
      const key = tabKey(tab);
      switch (tab.kind) {
        case 'pending':
          if (isPanelDirty(key) && !window.confirm(DISCARD_PENDING_CONFIRMATION)) return;
          dispatch({ type: 'discardPending', key });
          break;
        case 'saved':
          capture('canvas_tab_closed', { canvasId: tab.canvasId });
          dispatch({ type: 'closeTab', canvasId: tab.canvasId });
          break;
      }
      const next = neighbourOf(tabs, key);
      if (next !== undefined && isShownTab(shownTab, key)) showTab(next);
    },
    [capture, isPanelDirty, showTab]
  );

  const handleReorderTabs = useCallback((keys: readonly string[]) => dispatch({ type: 'reorderTabs', keys }), []);

  const handlePendingTitleChange = useCallback(
    (key: string, title: string) => dispatch({ type: 'retitlePending', key, title }),
    []
  );

  const handlePendingCanvasSaved = useCallback(
    (key: string, { session_id: createdSessionId, canvas }: CreatedCanvas) => {
      const {
        sessionId,
        searchParams,
        shownTab,
        urlVersionId,
        onSessionCreated,
        onCanvasCreated,
        onPendingSavedInSession
      } = latestRef.current;
      const isNewSession = createdSessionId !== sessionId;
      // A save that lands after the user moved to another tab leaves that tab shown.
      const isStillShown = isShownTab(shownTab, key);
      capture('canvas_created_from_new_document', { canvasId: canvas.id, sessionId: createdSessionId, isNewSession });
      // Listed straight away, so its tab keeps its title while the list refetches.
      queryClient.setQueryData<CanvasSummary[]>(editorCanvasesQueryKey(createdSessionId), (old = []) =>
        hasCanvas(old, canvas.id) ? old : [...old, { id: canvas.id, title: canvas.title }]
      );
      queryClient.invalidateQueries({ queryKey: editorCanvasesQueryKey(createdSessionId) });
      dispatch({ type: 'pendingSaved', key, sessionId: createdSessionId, canvasId: canvas.id });
      if (isNewSession) {
        const search = isStillShown
          ? withUrlTab(searchParams, savedTab(canvas.id), null)
          : withUrlTab(searchParams, shownTab, urlVersionId);
        onSessionCreated?.(createdSessionId, `?${search}`, canvas.title);
      } else {
        if (isStillShown) writeUrlTab(savedTab(canvas.id));
        onCanvasCreated?.();
        onPendingSavedInSession?.(canvas.title);
      }
    },
    [capture, queryClient, writeUrlTab]
  );

  const adoptCreatedSession = useCallback(
    (createdSessionId: string) => dispatch({ type: 'adoptSession', sessionId: createdSessionId }),
    []
  );

  const getSearchToCarry = useCallback(
    () => withoutDeadPending(window.location.search, latestRef.current.tabsState),
    []
  );

  const abandonDocuments = useCallback(() => dispatch({ type: 'abandon' }), []);

  const saveEditorBeforeSend = useCallback(async (): Promise<SavedPendingCanvas | null> => {
    const { isReadOnly, currentPanelKey, tabsState } = latestRef.current;
    if (isReadOnly) return null;
    // Every canvas's edits, not only the shown one's: the turn may edit any of them, and unsaved edits would hide that.
    await Promise.all(
      tabsState.canvasPanels
        .filter(({ key }) => key !== currentPanelKey)
        .map(({ key }) => documentHandlesRef.current.get(key)?.save())
    );
    if (currentPanelKey === null) return null;
    const created = (await getShownPanel()?.save()) ?? null;
    return created && { key: currentPanelKey, created };
  }, [getShownPanel]);

  const getCanvasContext = useCallback((): { editor_canvas_id: string } | null => {
    const { isEditorEnabled, activeCanvasId } = latestRef.current;
    if (!isEditorEnabled || !activeCanvasId) return null;
    return { editor_canvas_id: activeCanvasId };
  }, []);

  /** Called by the stream handler when a canvas tool call completes. */
  const handleCanvasToolComplete = useCallback(
    (toolName: string, metadata: Record<string, any>) => {
      const { isEditorEnabled, sessionId, onCanvasCreated } = latestRef.current;
      if (!isEditorEnabled || !CANVAS_TOOL_NAMES.has(toolName)) return;

      const canvasId = metadata?.canvas_id;
      if (!canvasId) return;

      capture('canvas_ai_tool_used', { canvasId, toolName });
      queryClient.invalidateQueries({ queryKey: editorCanvasesQueryKey(sessionId) });

      if (CANVAS_CREATE_TOOLS.has(toolName)) {
        showCanvasLive(canvasId);
        onCanvasCreated?.();
        void queryClient.invalidateQueries({ queryKey: ['canvas-document-target'] });
      } else if (CANVAS_EDIT_TOOLS.has(toolName)) {
        // Refresh editor content by invalidating queries
        if (sessionId) {
          const canvasRefetch = queryClient.invalidateQueries({ queryKey: editorCanvasQueryKey(sessionId, canvasId) });
          queryClient.invalidateQueries({ queryKey: editorSessionCanvasVersionsQueryKey(sessionId) });
          void queryClient.invalidateQueries({ queryKey: ['canvas-document-target'] });
          // Keep the editing state until the refetched content replaces the stale content.
          setRefetchingEditedCanvasIds((ids) => [...ids, canvasId]);
          canvasRefetch.finally(() =>
            setRefetchingEditedCanvasIds((ids) => ids.filter((_, index) => index !== ids.indexOf(canvasId)))
          );
        }
      } else if (toolName === 'open_canvas') {
        showCanvasLive(canvasId);
      }
    },
    [showCanvasLive, queryClient, capture]
  );

  const isEditingActiveCanvas =
    activeCanvasId !== null &&
    (refetchingEditedCanvasIds.some((id) => isSameCanvasId(id, activeCanvasId)) ||
      (streamingMessage?.parts.some(isRunningEditOf(activeCanvasId)) ?? false));
  const turnLock: TurnLock = !isStreamRunning ? null : isEditingActiveCanvas ? 'editing' : 'responding';

  // Close editor when feature transitions from enabled → disabled (e.g. viewport crossed mobile
  // breakpoint — window.innerWidth also shrinks from browser/OS zoom, not just window resizing)
  // and reopen it when the feature transitions back, so a zoom/resize round-trip doesn't strand
  // the user without the tab they had open. A ref tracks the previous enabled state so we
  // don't clear the URL canvas param on initial mount when the feature flag / profile data
  // hasn't loaded yet. The remembered tab is distinct from a manual close (userClosedEditorRef),
  // which must never be auto-restored.
  const prevEditorEnabledRef = useRef(isEditorEnabled);
  const autoClosedTabRef = useRef<{
    tab: ShownTab;
    versionId: number | null;
    sessionId: string | undefined;
  } | null>(null);
  useEffect(() => {
    const wasEnabled = prevEditorEnabledRef.current;
    prevEditorEnabledRef.current = isEditorEnabled;
    const { tabsState, urlTab, urlVersionId, sessionId, isReadOnly } = latestRef.current;

    if (wasEnabled && !isEditorEnabled) {
      // The pane stays mounted, hidden, until the editors are dropped below, so
      // save what can be saved. A Pending Canvas cannot be saved without the
      // user, and the breakpoint cannot be refused, so one with typed content is
      // kept, hidden, for the way back unless the user agrees to discard it.
      if (!isReadOnly) {
        for (const { key } of tabsState.canvasPanels) {
          const panel = documentHandlesRef.current.get(key);
          if (!panel?.isDirty()) continue;
          panel.save().catch(() => {
            toast.warn('Could not auto-save document before closing the editor');
          });
        }
      }
      const keepPendings =
        tabsState.pendings.some(({ key }) => isPanelDirty(key)) && !window.confirm(DISCARD_PENDINGS_CONFIRMATION);
      dispatch({ type: 'unmountEditors', keepPendings });
      if (urlTab !== null) {
        autoClosedTabRef.current = { tab: urlTab, versionId: urlVersionId, sessionId };
        writeUrlTab(null);
      }
    } else if (!wasEnabled && isEditorEnabled && urlTab === null && !userClosedEditorRef.current) {
      const restored = autoClosedTabRef.current;
      autoClosedTabRef.current = null;
      // A remembered tab from another session must never leak into this one, and
      // a Pending Canvas discarded on the way out has nothing to restore.
      if (
        restored &&
        restored.sessionId === sessionId &&
        (restored.tab.kind === 'saved' || findPending(tabsState, restored.tab.key))
      ) {
        // pendingMode is re-derived from ?canvasVersion= by the effect below once this lands in the URL.
        writeUrlTab(restored.tab, restored.versionId);
      }
    }
  }, [isEditorEnabled, isPanelDirty, writeUrlTab]);

  // Apply ?canvasVersion= from the URL (hub backlinks, refresh, browser back).
  // Keep the same object when the intent is unchanged so the panel does not refetch.
  useEffect(() => {
    if (!activeCanvasId) return;
    if (urlVersionId === null) {
      setPendingMode((prev) => (prev?.kind === 'viewing-version' && prev.canvasId === activeCanvasId ? null : prev));
      return;
    }
    setPendingMode((prev) => {
      if (prev?.kind === 'viewing-version' && prev.canvasId === activeCanvasId && prev.versionId === urlVersionId) {
        return prev;
      }
      return { canvasId: activeCanvasId, kind: 'viewing-version', versionId: urlVersionId };
    });
  }, [activeCanvasId, urlVersionId]);

  // Neither the mirrored mode nor an unconsumed intent may outlive the shown
  // canvas, whichever route hid it: cards would keep a reversed label, and a
  // later show would replay the intent. Keyed on the param so a session switch
  // or browser back counts too.
  useEffect(() => {
    if (activeCanvasId === null) {
      setActiveMode(null);
      setPendingMode(null);
    }
  }, [activeCanvasId]);

  // `?canvas=pending-…` outlives its Pending Canvas across a reload or a history
  // entry. Built from the current location rather than the last render's, so a
  // navigation this render has not seen yet, as into a new session, is kept.
  useEffect(() => {
    if (canvasParam === null || !isPendingKey(canvasParam) || findPending(tabsState, canvasParam)) return;
    const { pathname, search, hash } = window.location;
    const cleaned = withoutDeadPending(search, tabsState);
    if (cleaned !== search) navigate({ pathname, search: cleaned, hash }, { replace: true });
  }, [canvasParam, tabsState, navigate]);

  // Leaving the chat abandons its documents. A session created from this chat
  // (its first message or document) was adopted beforehand, so it keeps them.
  // Layout effects, so the shown tab's editor exists before the first paint.
  useLayoutEffect(() => {
    userClosedEditorRef.current = false;
    dispatch({ type: 'enterSession', sessionId, remembered: loadRememberedArrangement(sessionId) });
  }, [sessionId]);

  // However a closed canvas came to be shown (Closed Documents, a card, the URL), its tab reopens.
  useLayoutEffect(() => {
    if (activeCanvasId !== null) dispatch({ type: 'reopenTab', canvasId: activeCanvasId });
  }, [activeCanvasId, arrangement]);

  useLayoutEffect(() => {
    if (!isEditorEnabled || activeCanvasId === null || !sessionId) return;
    dispatch({ type: 'mountCanvas', sessionId, canvasId: activeCanvasId });
  }, [isEditorEnabled, activeCanvasId, sessionId]);

  return {
    activeCanvasId,
    isPanelOpen,
    canOpenPanel,
    tabs,
    closedDocuments,
    shownTab,
    panels,
    shownPanelKey: currentPanelKey,
    registerDocument,
    pendingMode,
    activeMode,
    handleOpenEditor,
    handleSelectTab,
    handleShowChanges,
    handleViewVersion,
    handleExitReadOnly,
    handleViewChange,
    handleCloseEditor,
    handleOpenPanel,
    handleNewDocument,
    handleCloseTab,
    handleReorderTabs,
    handlePendingTitleChange,
    handlePendingCanvasSaved,
    adoptCreatedSession,
    getSearchToCarry,
    abandonDocuments,
    confirmDiscardUnsavedChanges,
    saveEditorBeforeSend,
    getCanvasContext,
    handleCanvasToolComplete,
    turnLock
  };
}
